import React, { useState, useEffect, useRef, useCallback } from 'react';
import { agentAPI } from '../services/api';
import './AgentChat.css';

// ─── Simple Markdown renderer (bold, italic, lists, code) ────────────────────
const renderMarkdown = (text) => {
    if (!text) return '';
    return text
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code>$1</code>')
        .replace(/^### (.+)$/gm, '<h4>$1</h4>')
        .replace(/^## (.+)$/gm, '<h3>$1</h3>')
        .replace(/^- (.+)$/gm, '<li>$1</li>')
        .replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>')
        .replace(/\n\n/g, '<br/><br/>')
        .replace(/\n/g, '<br/>');
};

// ─── Typing indicator dots ────────────────────────────────────────────────────
const TypingIndicator = () => (
    <div className="agent-typing-indicator">
        <span></span><span></span><span></span>
        <small>HK2000 réfléchit…</small>
    </div>
);

// ─── Single message bubble ────────────────────────────────────────────────────
const MessageBubble = ({ msg }) => {
    const isUser = msg.role === 'user';
    const time   = new Date(msg.created_at || Date.now()).toLocaleTimeString('fr-DZ', { hour: '2-digit', minute: '2-digit' });

    return (
        <div className={`agent-msg ${isUser ? 'agent-msg--user' : 'agent-msg--assistant'}`}>
            {!isUser && (
                <div className="agent-msg__avatar">
                    <img src="/agent-icon.png" alt="HK" className="agent-avatar-img" />
                </div>
            )}
            <div className="agent-msg__bubble">
                {!isUser && <div className="agent-msg__name">HK2000</div>}
                <div
                    className="agent-msg__text"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                />
                <div className="agent-msg__time">{time}</div>
            </div>
        </div>
    );
};

// ─── Suggested quick questions ────────────────────────────────────────────────
const QUICK_QUESTIONS = [
    "📊 Statistiques du mois",
    "📋 Sinistres en cours",
    "💰 Honoraires impayés",
    "🚗 Derniers dossiers créés",
];

// ─── Main AgentChat Component ─────────────────────────────────────────────────
const AgentChat = () => {
    const [isOpen,    setIsOpen]    = useState(false);
    const [messages,  setMessages]  = useState([]);
    const [input,     setInput]     = useState('');
    const [loading,   setLoading]   = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [hasNew,    setHasNew]    = useState(false);
    const [showSugg,  setShowSugg]  = useState(true);

    const messagesEndRef = useRef(null);
    const inputRef       = useRef(null);
    const chatPanelRef   = useRef(null);

    // Generate or restore session ID
    useEffect(() => {
        const stored = sessionStorage.getItem('habib_session_id');
        if (stored) {
            setSessionId(stored);
            loadHistory(stored);
        } else {
            const newId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            setSessionId(newId);
            sessionStorage.setItem('habib_session_id', newId);
            // Welcome message
            setMessages([{
                role: 'assistant',
                content: "👋 Bonjour ! Je suis **HK2000**, votre assistant IA de HabibExpertiseSoft.\n\nJe peux vous aider à :\n- 🔍 Rechercher des sinistres et dossiers\n- 📊 Consulter vos statistiques en temps réel\n- 💰 Vérifier les honoraires en attente\n- 📋 Obtenir des détails sur vos rapports\n\nQue puis-je faire pour vous ?",
                created_at: new Date().toISOString()
            }]);
        }
    }, []);

    // Load history from server
    const loadHistory = async (sid) => {
        try {
            const res = await agentAPI.getHistory(sid);
            if (res.data?.messages?.length > 0) {
                setMessages(res.data.messages);
                setShowSugg(false);
            } else {
                setMessages([{
                    role: 'assistant',
                    content: "👋 Bonjour ! Je suis **HK2000**, votre assistant IA de HabibExpertiseSoft.\n\nQue puis-je faire pour vous ?",
                    created_at: new Date().toISOString()
                }]);
            }
        } catch {
            // Ignore, show default welcome
        }
    };

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    // Focus input when opened
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
            setHasNew(false);
        }
    }, [isOpen]);

    // Send message
    const sendMessage = useCallback(async (text) => {
        const trimmed = (text || input).trim();
        if (!trimmed || loading) return;

        const userMsg = { role: 'user', content: trimmed, created_at: new Date().toISOString() };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setLoading(true);
        setShowSugg(false);

        try {
            const res = await agentAPI.chat({
                message:   trimmed,
                sessionId: sessionId,
                testMode:  false
            });

            const assistantMsg = {
                role:       'assistant',
                content:    res.data?.response || "Désolé, je n'ai pas pu traiter votre demande.",
                created_at: new Date().toISOString()
            };
            setMessages(prev => [...prev, assistantMsg]);

            // Notify if panel is closed
            if (!isOpen) setHasNew(true);

        } catch (err) {
            const errContent = err.response?.data?.error === 'N8N_UNAVAILABLE'
                ? "⚠️ L'assistant est temporairement indisponible. Vérifiez que le workflow N8N est actif."
                : "❌ Erreur de connexion. Veuillez réessayer dans quelques secondes.";

            setMessages(prev => [...prev, {
                role: 'assistant', content: errContent, created_at: new Date().toISOString()
            }]);
        } finally {
            setLoading(false);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [input, loading, sessionId, isOpen]);

    // Handle Enter key
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // New conversation
    const newConversation = async () => {
        try {
            await agentAPI.clearHistory(sessionId);
        } catch { /* ignore */ }
        const newId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        setSessionId(newId);
        sessionStorage.setItem('habib_session_id', newId);
        setMessages([{
            role: 'assistant',
            content: "✨ Nouvelle conversation démarrée ! Comment puis-je vous aider ?",
            created_at: new Date().toISOString()
        }]);
        setShowSugg(true);
    };

    return (
        <>
            {/* ── Floating Button ── */}
            <button
                id="habib-agent-btn"
                className={`agent-fab ${isOpen ? 'agent-fab--open' : ''} ${hasNew ? 'agent-fab--pulse' : ''}`}
                onClick={() => setIsOpen(v => !v)}
                title="HK2000 - Assistant IA"
                aria-label="Ouvrir l'assistant HK2000"
            >
                {isOpen ? (
                    <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                ) : (
                    <img src="/agent-icon.png" alt="HK" className="agent-fab-icon" />
                )}
                {hasNew && !isOpen && <span className="agent-fab__badge">1</span>}
                <span className="agent-fab__label">HK2000</span>
            </button>

            {/* ── Chat Panel ── */}
            <div
                ref={chatPanelRef}
                id="habib-agent-panel"
                className={`agent-panel ${isOpen ? 'agent-panel--open' : ''}`}
                role="dialog"
                aria-label="Chat HK2000 IA"
            >
                {/* Header */}
                <div className="agent-panel__header">
                    <div className="agent-panel__header-left">
                        <div className="agent-panel__avatar">
                            <img src="/agent-icon.png" alt="HK2000" className="agent-avatar-img" />
                            <div className="agent-panel__status" title="En ligne"></div>
                        </div>
                        <div>
                            <div className="agent-panel__name">HK2000</div>
                            <div className="agent-panel__subtitle">Intelligence Artificielle · CRM</div>
                        </div>
                    </div>
                    <div className="agent-panel__header-actions">
                        <button
                            className="agent-panel__action-btn"
                            onClick={newConversation}
                            title="Nouvelle conversation"
                        >
                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M12 5v14M5 12h14"/>
                            </svg>
                        </button>
                        <button
                            className="agent-panel__action-btn"
                            onClick={() => setIsOpen(false)}
                            title="Fermer"
                        >
                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Messages area */}
                <div className="agent-panel__messages" id="habib-messages-container">
                    {messages.map((msg, i) => (
                        <MessageBubble key={i} msg={msg} />
                    ))}
                    {loading && <TypingIndicator />}

                    {/* Quick suggestions */}
                    {showSugg && !loading && messages.length <= 1 && (
                        <div className="agent-suggestions">
                            <p className="agent-suggestions__label">Suggestions rapides :</p>
                            <div className="agent-suggestions__grid">
                                {QUICK_QUESTIONS.map((q, i) => (
                                    <button
                                        key={i}
                                        className="agent-suggestions__btn"
                                        onClick={() => sendMessage(q.replace(/^[^\s]+ /, ''))}
                                    >
                                        {q}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="agent-panel__footer">
                    <div className="agent-input-wrap">
                        <textarea
                            ref={inputRef}
                            id="habib-chat-input"
                            className="agent-input"
                            placeholder="Posez votre question à HK2000…"
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            rows={1}
                            disabled={loading}
                            aria-label="Message pour HK2000"
                        />
                        <button
                            id="habib-send-btn"
                            className={`agent-send-btn ${input.trim() ? 'agent-send-btn--active' : ''}`}
                            onClick={() => sendMessage()}
                            disabled={loading || !input.trim()}
                            aria-label="Envoyer"
                        >
                            {loading ? (
                                <div className="agent-spinner"></div>
                            ) : (
                                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path d="M22 2L11 13M22 2L15 22 11 13 2 9l20-7z"/>
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};


export default AgentChat;
